import React from 'react';
import { useI18n } from '../../context/I18nContext';
import { getCableDisplayName } from '../../utils/cableLogic';
import { validateCable } from '../../utils/validation';
import CableTessera from './CableTessera';
import WarningIcon from '../ui/WarningIcon';

export const MacroTessera = ({
  siglatura,
  groupData,
  onCableClick,
  onEditCable,
  onEditMultipolarCable,
  onDeleteCable,
  onDeleteMultipolarCable,
  settings,
  isDragEnabled,
  displaySiglatura,
  handleDragStart,
  handleDragEnd,
  handleDragOver,
  handleDragEnter,
  handleDragLeave,
  handleDrop,
  draggedIndex,
  dragOverIndex,
  editingIndex,
  editingMultipolarIndex,
  showDeleteButtons,
  onToggleDelete,
  isSelectionModeEnabled,
  availableSheaths = [],
  selectedCables,
  onToggleSelection
}) => {
  const { t } = useI18n();
  const { cables, isMultiple, isSheathGroup, sheathId } = groupData;

  const hasIssues = cables.some(cable => {
    const validation = validateCable(cable, cables, settings.lingua);
    return !validation.isValid;
  });
  
  // Trova la guaina se è un gruppo guaina
  const sheath = isSheathGroup && sheathId ? availableSheaths.find(s => s.id === sheathId) : null;

  if (!isMultiple || cables.length === 1) {
    return (
      <CableTessera
        cable={cables[0]}
        validateCable={(c) => validateCable(c, cables, settings.lingua)}
        displaySiglatura={displaySiglatura || getCableDisplayName(cables[0], settings.lingua, settings)}
        onCableClick={onCableClick}
        onEditCable={onEditCable}
        onEditMultipolarCable={onEditMultipolarCable}
        onDeleteCable={onDeleteCable}
        onDeleteMultipolarCable={onDeleteMultipolarCable}
        showSiglatura={true}
        clickable={false}
        settings={settings}
        isDragEnabled={isDragEnabled}
        editingIndex={editingIndex}
        editingMultipolarIndex={editingMultipolarIndex}
        showDeleteButton={showDeleteButtons.has(cables[0].originalIndex)}
        onToggleDelete={() => onToggleDelete(cables[0].originalIndex)}
        isSelectionModeEnabled={isSelectionModeEnabled}
        selectedCables={selectedCables}
        onToggleSelection={onToggleSelection}
        availableSheaths={availableSheaths}
      />
    );
  }

  const getMacroTesseraTitle = () => {
    // Titolo speciale per gruppi guaina
    if (isSheathGroup && sheath) {
      return sheath.fullLabel || `${t('cableList.sheathLabel')} ${sheath.label}`;
    }
    
    if (settings.marcaturePerCollegamento) {
      const cableNumbers = cables
        .map(cable => `#${cable.originalIndex + 1}`)
        .join(', ');
      return `${t('cableList.cableLabel')} ${cableNumbers}`;
    } else {
      return `${t('cableList.cableLabel')} ${displaySiglatura}` || `${t('cableList.cableLabel')} ${siglatura}`;
    }
  };

  return (
    <div className={`border rounded-lg p-3 transition-colors cursor-pointer ${
      hasIssues
        ? 'bg-amber-50 border-amber-200 hover:bg-amber-100'
        : isSheathGroup
          ? 'bg-sky-50 border-sky-200 hover:bg-sky-100'
          : 'bg-green-50 border-green-200 hover:bg-green-100'
      }`}
      onClick={() => onCableClick && onCableClick(siglatura, cables[0].originalIndex)}
    >
      {/* Header della macrotessera */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          {/* Checkbox per selezione del gruppo */}
          {isSelectionModeEnabled && (
            <label className="flex items-center cursor-pointer flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={cables.some(cable => selectedCables.has(cable.originalIndex))}
                onChange={() => {
                  // Se tutti i cavi del gruppo sono selezionati, deseleziona tutti
                  // Altrimenti seleziona tutti
                  const allSelected = cables.every(cable => selectedCables.has(cable.originalIndex));
                  cables.forEach(cable => {
                    if (allSelected) {
                      onToggleSelection(cable.originalIndex);
                    } else if (!selectedCables.has(cable.originalIndex)) {
                      onToggleSelection(cable.originalIndex);
                    }
                  });
                }}
                className="w-5 h-5 text-green-600 border-2 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
              />
            </label>
          )}
          
          <span className="font-medium text-base">
            {getMacroTesseraTitle()}
          </span>

          <span className="text-sm text-gray-500">
            {cables.length} {isSheathGroup ? t('cableList.cables') : t('cableList.connectedCables')}
          </span>

          {hasIssues && (
            <span className="text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
              ⚠️ {t('cableList.hasProblems')}
            </span>
          )}
        </div>

        {/* Badge "Guaina" o "Multiplo" */}
        {isSheathGroup ? (
          <div className="bg-sky-100 text-sky-800 px-3 py-1 rounded-full text-sm font-medium">
            {t('cableList.sheath')}
          </div>
        ) : (
          <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
            {t('cableList.multiple')}
          </div>
        )}
      </div>

      {/* Lista cavi con riga verde (o sky/blu per guaine) */}
      <div className={`space-y-3 pl-4 border-l-2 ${isSheathGroup ? 'border-sky-400' : 'border-green-300'}`}>
        {cables.map((cable) => {
          const isGlobalDragging = draggedIndex === cable.originalIndex;
          const isGlobalDragOver = dragOverIndex === cable.originalIndex && draggedIndex !== cable.originalIndex;
          const validation = validateCable(cable, cables, settings.lingua);
          const hasInnerIssues = !validation.isValid;

          return (
            <div
              key={cable.originalIndex}
              className="relative"
              draggable={isDragEnabled}
              onDragStart={(e) => handleDragStart(e, cable.originalIndex)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, cable.originalIndex)}
              onDragEnter={(e) => handleDragEnter(e, cable.originalIndex)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, cable.originalIndex)}
              style={{
                transform: isGlobalDragging ? 'scale(0.95)' : isGlobalDragOver ? 'translateY(-1px)' : 'none',
                boxShadow: isGlobalDragOver ? '0 4px 8px rgba(59, 130, 246, 0.3)' :
                  hasInnerIssues ? '0 2px 4px rgba(245, 158, 11, 0.2)' : 'none'
              }}
            >
              {/* Icone drag/warning */}
              {isDragEnabled && (
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-green-600 transition-colors z-10"> {/* ✅ Hover verde */}
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M7 2a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 14a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM17 2a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM17 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM17 14a2 2 0 1 1-4 0 2 2 0 0 1 4 0z" />
                  </svg>
                </div>
              )}

              {!isDragEnabled && hasInnerIssues && (
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
                  <WarningIcon
                    issues={validation.errors}
                    className="flex-shrink-0"
                  />
                </div>
              )}

              <CableTessera
                cable={cable}
                validateCable={(c) => validateCable(c, cables, settings.lingua)}
                displaySiglatura={getCableDisplayName(cable, settings.lingua, settings)}
                onCableClick={null}
                onEditCable={onEditCable}
                onEditMultipolarCable={onEditMultipolarCable}
                onDeleteCable={onDeleteCable}
                onDeleteMultipolarCable={onDeleteMultipolarCable}
                showSiglatura={isSheathGroup ? true : settings.marcaturePerCollegamento}
                clickable={false}
                settings={settings}
                isDragEnabled={isDragEnabled}
                isDragging={isGlobalDragging}
                isDragOver={isGlobalDragOver}
                hasIssues={hasInnerIssues}
                editingIndex={editingIndex}
                editingMultipolarIndex={editingMultipolarIndex}
                showDeleteButton={showDeleteButtons && showDeleteButtons.has(cable.originalIndex)}
                onToggleDelete={() => onToggleDelete && onToggleDelete(cable.originalIndex)}
                isSelectionModeEnabled={isSelectionModeEnabled}
                selectedCables={selectedCables}
                onToggleSelection={onToggleSelection}
                availableSheaths={availableSheaths}
                hideSheathBadge={isSheathGroup}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
